import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, formatDistanceToNow, addDays } from 'date-fns';
import {
  tokensAPI,
  PAT_SCOPES,
  type PersonalAccessToken,
  type CreatedPersonalAccessToken,
  type CreateTokenRequest,
} from '../../api/tokens';
import { useToast } from '../../hooks/useToast';
import { logger } from '../../utils/logger';
import Modal, { ConfirmModal } from '../Modal/Modal';
import { Input, Badge } from '../ui';
import styles from './AccessTokensPanel.module.css';

type ExpiryPreset = 'never' | '30d' | '90d' | '1y';

const EXPIRY_OPTIONS: Array<{ value: ExpiryPreset; label: string }> = [
  { value: 'never', label: 'Never' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: '1y', label: '1 year' },
];

const DEFAULT_SCOPES = ['read'];

interface CreateFormState {
  name: string;
  description: string;
  scopes: string[];
  expiry: ExpiryPreset;
}

const emptyForm: CreateFormState = {
  name: '',
  description: '',
  scopes: [...DEFAULT_SCOPES],
  expiry: 'never',
};

function expiryPresetToIso(preset: ExpiryPreset): string | null {
  switch (preset) {
    case '30d':
      return addDays(new Date(), 30).toISOString();
    case '90d':
      return addDays(new Date(), 90).toISOString();
    case '1y':
      return addDays(new Date(), 365).toISOString();
    default:
      return null;
  }
}

type TokenStatus = 'active' | 'revoked' | 'expired';

function tokenStatus(token: PersonalAccessToken): TokenStatus {
  if (!token.is_active) return 'revoked';
  if (token.expires_at && new Date(token.expires_at).getTime() < Date.now()) return 'expired';
  return 'active';
}

const STATUS_LABEL: Record<TokenStatus, string> = {
  active: 'Active',
  revoked: 'Revoked',
  expired: 'Expired',
};

const STATUS_VARIANT: Record<TokenStatus, 'success' | 'secondary' | 'warning'> = {
  active: 'success',
  revoked: 'secondary',
  expired: 'warning',
};

function formatDate(iso: string | null): string {
  if (!iso) return 'Never';
  try {
    return format(new Date(iso), 'MMM d, yyyy');
  } catch {
    return iso;
  }
}

function formatRelative(iso: string | null): string {
  if (!iso) return 'Never';
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

function mcpUrl(): string {
  const base = (import.meta.env.VITE_API_URL as string | undefined) || 'http://localhost:8000';
  return `${base}/mcp`;
}

function connectionSnippet(token: string): string {
  return `claude mcp add --transport http lazysem ${mcpUrl()} --header "Authorization: Bearer ${token}"`;
}

export default function AccessTokensPanel() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [form, setForm] = useState<CreateFormState>(emptyForm);
  const [revokeTarget, setRevokeTarget] = useState<PersonalAccessToken | null>(null);
  const [createdToken, setCreatedToken] = useState<CreatedPersonalAccessToken | null>(null);
  const [copiedField, setCopiedField] = useState<'token' | 'snippet' | null>(null);

  const {
    data: tokens,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['personal-access-tokens'],
    queryFn: tokensAPI.list,
  });

  const createMutation = useMutation<CreatedPersonalAccessToken, Error, CreateTokenRequest>({
    mutationFn: tokensAPI.create,
    onSuccess: (created) => {
      setCreatedToken(created);
      setForm(emptyForm);
      setShowCreateForm(false);
      queryClient.invalidateQueries({ queryKey: ['personal-access-tokens'] });
      toast.success('Token created', `"${created.name}" was created successfully.`);
    },
    onError: (error) => {
      logger.error('Failed to create personal access token:', error);
      toast.error('Failed to create token', error?.message || 'Unknown error');
    },
  });

  const revokeMutation = useMutation<{ success: boolean }, Error, string>({
    mutationFn: (id: string) => tokensAPI.revoke(id),
    onSuccess: (_result, id) => {
      queryClient.invalidateQueries({ queryKey: ['personal-access-tokens'] });
      const target = tokens?.find((t) => t.id === id);
      toast.success('Token revoked', target ? `"${target.name}" has been revoked.` : undefined);
    },
    onError: (error) => {
      logger.error('Failed to revoke personal access token:', error);
      toast.error('Failed to revoke token', error?.message || 'Unknown error');
    },
    onSettled: () => {
      setRevokeTarget(null);
    },
  });

  const toggleScope = (scope: string) => {
    setForm((f) => ({
      ...f,
      scopes: f.scopes.includes(scope)
        ? f.scopes.filter((s) => s !== scope)
        : [...f.scopes, scope],
    }));
  };

  const handleCreate = () => {
    const name = form.name.trim();
    if (!name) {
      toast.error('Please enter a token name');
      return;
    }
    if (name.length > 100) {
      toast.error('Token name must be 100 characters or fewer');
      return;
    }
    if (form.scopes.length === 0) {
      toast.error('Select at least one scope');
      return;
    }
    createMutation.mutate({
      name,
      description: form.description.trim() || undefined,
      scopes: form.scopes,
      expires_at: expiryPresetToIso(form.expiry),
    });
  };

  const handleRevokeConfirmed = () => {
    if (!revokeTarget) return;
    revokeMutation.mutate(revokeTarget.id);
  };

  const handleCopy = async (field: 'token' | 'snippet', value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField((current) => (current === field ? null : current)), 2000);
    } catch (error) {
      logger.error('Failed to copy to clipboard:', error);
      toast.error('Could not copy — select and copy manually');
    }
  };

  const closeCreatedModal = () => {
    setCreatedToken(null);
    setCopiedField(null);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h3 className={styles.title}>Personal Access Tokens</h3>
          <p className={styles.subtitle}>
            Let AI assistants like Claude or Cursor securely access your LazySEM data via MCP.
          </p>
        </div>
        <div className={styles.headerActions}>
          <button
            className={styles.secondaryButton}
            onClick={() => setShowCreateForm((v) => !v)}
          >
            {showCreateForm ? 'Cancel' : '+ Create token'}
          </button>
        </div>
      </div>

      {showCreateForm && (
        <div className={styles.inlineForm}>
          <h4 className={styles.formTitle}>Create a new token</h4>
          <div className={styles.formGrid}>
            <Input
              label="Name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Claude Desktop"
              maxLength={100}
              required
              fullWidth
            />
            <label className={styles.formField}>
              <span>Description (optional)</span>
              <input
                type="text"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="What is this token for?"
                maxLength={500}
              />
            </label>
            <label className={styles.formField}>
              <span>Expiry</span>
              <select
                value={form.expiry}
                onChange={(e) => setForm((f) => ({ ...f, expiry: e.target.value as ExpiryPreset }))}
              >
                {EXPIRY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className={styles.scopeGroup}>
            <span className={styles.scopeGroupLabel}>Scopes</span>
            {PAT_SCOPES.map((scope) => (
              <label key={scope.value} className={styles.scopeOption}>
                <input
                  type="checkbox"
                  checked={form.scopes.includes(scope.value)}
                  onChange={() => toggleScope(scope.value)}
                />
                <span>
                  <span className={styles.scopeName}>{scope.label}</span>
                  <span className={styles.scopeHelp}>{scope.helpText}</span>
                </span>
              </label>
            ))}
          </div>

          <div className={styles.formActions}>
            <button
              className={styles.primaryButton}
              onClick={handleCreate}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating…' : 'Create token'}
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className={styles.loading}>Loading tokens…</div>
      ) : isError ? (
        <div className={styles.error}>Failed to load personal access tokens.</div>
      ) : !tokens || tokens.length === 0 ? (
        <div className={styles.empty}>
          Personal Access Tokens let AI assistants (Claude, Cursor, …) securely access your
          LazySEM data via MCP. See docs/MCP_SERVER.md for setup instructions.
        </div>
      ) : (
        <div className={styles.list}>
          {tokens.map((token) => {
            const status = tokenStatus(token);
            return (
              <div key={token.id} className={styles.card}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardTitleRow}>
                    <span className={styles.tokenName}>{token.name}</span>
                    <Badge variant={STATUS_VARIANT[status]} size="sm">
                      {STATUS_LABEL[status]}
                    </Badge>
                  </div>
                  <div className={styles.cardActions}>
                    <button
                      className={styles.dangerButton}
                      onClick={() => setRevokeTarget(token)}
                      disabled={status === 'revoked' || revokeMutation.isPending}
                    >
                      Revoke
                    </button>
                  </div>
                </div>
                {token.description && (
                  <p className={styles.tokenDescription}>{token.description}</p>
                )}
                <div className={styles.tokenPrefix}>
                  <code>{token.key_prefix}…</code>
                </div>
                <div className={styles.scopeBadges}>
                  {token.scopes.map((scope) => (
                    <Badge key={scope} variant="secondary" size="sm">
                      {scope}
                    </Badge>
                  ))}
                </div>
                <div className={styles.cardMeta}>
                  <span>Created {formatDate(token.created_at)}</span>
                  <span>Last used {formatRelative(token.last_used_at)}</span>
                  <span>Expires {formatDate(token.expires_at)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        isOpen={!!createdToken}
        onClose={closeCreatedModal}
        title="Token created"
        size="medium"
      >
        {createdToken && (
          <div className={styles.secretModalBody}>
            <p className={styles.secretWarning}>
              This token is shown only once — store it somewhere safe. You won't be able to see
              it again.
            </p>
            <div className={styles.secretRow}>
              <code className={styles.secretValue}>{createdToken.token}</code>
              <button
                className={styles.secondaryButton}
                onClick={() => handleCopy('token', createdToken.token)}
              >
                {copiedField === 'token' ? 'Copied ✓' : 'Copy'}
              </button>
            </div>

            <p className={styles.secretHint}>
              Connect this token to Claude with:
            </p>
            <div className={styles.secretRow}>
              <code className={styles.secretValue}>{connectionSnippet(createdToken.token)}</code>
              <button
                className={styles.secondaryButton}
                onClick={() => handleCopy('snippet', connectionSnippet(createdToken.token))}
              >
                {copiedField === 'snippet' ? 'Copied ✓' : 'Copy'}
              </button>
            </div>

            <div className={styles.formActions}>
              <button className={styles.primaryButton} onClick={closeCreatedModal}>
                Done
              </button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmModal
        isOpen={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onConfirm={handleRevokeConfirmed}
        title="Revoke token"
        message={`Revoke "${revokeTarget?.name}"? Any AI client using it will immediately lose access. This cannot be undone.`}
        confirmText="Revoke"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
}
